// IMPORTS
import { useState, useEffect, useContext } from "react";
import { useTranslation } from "react-i18next";

// COMPONENTS
import CustomButton from "../../components/CustomButton";
import CustomInput from "../../components/CustomInput";
import Loader from "../../components/Loader";
import ErrorBox from "../../components/ErrorBox";
import BackToDashboard from "../../components/BackToDashboard";
import ErrorMessage from "../../components/ErrorMessage";
import RoundedTable, {
  TABLE_CELL_TYPES,
} from "../../components/table-component/RoundedTable";
import PageHeadline from "../../components/PageHeadline";
import AreYouSureModal from "../../components/AreYouSureModal";
import InformationBox from "../../components/InformationBox";
import Dropdown from "../../components/Dropdown";
import HotDropdownV2 from "../../components/HotdropdownV2";
import AddBookModal from "./AddBookModal";
import EditBookModal from "./EditBookModal";
import PageWrapper from "../../components/PageWrapper";

// UTILS & SERVICES
import useLanguage from "../../utils/useLanguage";
import { UserAuth } from "../../utils/AuthContext";
import { DataContext } from "../../utils/DataContext";
import { getAllProducts, deleteProduct } from "../../services/productServices";
import { getAllEducationLevels } from "../../services/educationLevelServices";

// ASSETS
import nextIcon from "../../assets/next_icon.png";
import previousIcon from "../../assets/previous_icon.png";
import addIcon from "../../assets/add_icon_light.png";
import searchIcon from "../../assets/search_icon.png";
import infoIcon from "../../assets/information.png";
import infoBlue from "../../assets/info-blue.svg";

import Tooltip from "../../components/Tooltip";
import { Link } from "react-router-dom";

export default function BooksPage() {
  // HOOKS
  const { t } = useTranslation("general");
  const { currentDirection } = useLanguage();
  const { userData } = useContext(UserAuth);
  const { loading, setLoading } = useContext(DataContext);

  // STATES - Book Data
  const [books, setBooks] = useState([]);
  const [filteredBooks, setFilteredBooks] = useState([]);
  const [selectedBook, setSelectedBook] = useState(null);
  const [selectedBooks, setSelectedBooks] = useState([]);
  const [educationLevels, setEducationLevels] = useState([]);

  // STATES - Modal Control
  const [showAddBookModal, setShowAddBookModal] = useState(false);
  const [showEditBookModal, setShowEditBookModal] = useState(false);
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);

  // STATES - Search & Filter
  const [searchTerm, setSearchTerm] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedSearchType, setSelectedSearchType] = useState({
    title: "bookName",
    value: "bookName",
  });

  // STATES - UI Control
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalRecords: 0,
  });
  const [loadingModal, setLoadingModal] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  // API HANDLERS
  const fetchEducationLevels = async (
    page = 1,
    limit = 5,
    isDropDown = false,
    searchTerm
  ) => {
    try {
      const response = await getAllEducationLevels({
        page,
        limit,
        search: searchTerm,
      });

      const transformedData = (response.data.classes || []).map((level) => ({
        title: level.name,
        value: level.id,
        price: level.price,
      }));

      if (page === 1) {
        setEducationLevels(transformedData);
      } else {
        setEducationLevels((prev) => [...prev, ...transformedData]);
      }

      return {
        status: response.status || 200,
        data: {
          classes: transformedData,
          totalPages: response.data.totalPages,
          totalRecords: response.data.totalRecords,
        },
      };
    } catch (error) {
      console.error("Error fetching education levels:", error);
      return {
        status: 500,
        data: { classes: [], totalPages: 1, totalRecords: 0 },
      };
    }
  };

  const getBooks = async () => {
    try {
      setLoading(true);
      const searchParams = {
        name: selectedSearchType.value === "bookName" ? searchTerm : null,
        productSubType:
          selectedSearchType.value === "bookType" ? searchTerm.value : null,
        schoolClassId:
          selectedSearchType.value === "academicLevel"
            ? searchTerm.value
            : null,
      };

      const orgId = localStorage.getItem("organizationId");

      const response = await getAllProducts({
        page: pagination.currentPage,
        limit: 10,
        organizationId: orgId,
        ...searchParams,
        productType: "book",
      });

      if (response.data?.products) {
        setBooks(response.data.products);
        setFilteredBooks(response.data.products);
        setPagination((prev) => ({
          ...prev,
          totalPages: response.data.totalPages,
          totalRecords: response.data.totalRecords,
        }));

        if (response.data.products.length) {
          setSelectedBook(response.data.products[0]);
        }
      }
    } catch (error) {
      console.error("Failed to get books", error);
      setErrorMessage("failedToLoad");
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteBook = async (bookId) => {
    try {
      setLoadingModal(true);
      const response = await deleteProduct(bookId);

      if (response.status === 200) {
        await getBooks();
        setPagination((prev) => ({
          ...prev,
          totalRecords: prev.totalRecords - 1,
        }));
      }
    } catch (error) {
      console.error("Error deleting book:", error);
      setErrorMessage("failedToDelete");
    } finally {
      setShowDeleteConfirmation(false);
      setLoadingModal(false);
    }
  };

  // EVENT HANDLERS
  const openAddBookModal = () => {
    setShowAddBookModal(true);
  };

  const openEditBookModal = (book) => {
    setSelectedBook(book);
    setShowEditBookModal(true);
  };

  const confirmDeleteBook = (book) => {
    setSelectedBook(book);
    setShowDeleteConfirmation(true);
  };

  const handleSearchType = (selected) => {
    setSelectedSearchType(selected);
    setSearchQuery("");
    setSearchTerm("");

    // Reset dropdown state when switching to academicLevel
    if (selected.value === "academicLevel") {
      setEducationLevels([]); // Clear existing options
      fetchEducationLevels(1, 5, false, ""); // Fetch fresh data
    }
  };

  const handleSearch = (searchValue = null) => {
    const query = searchValue !== null ? searchValue : searchQuery;
    setSearchTerm(query);
  };

  // TABLE CONFIGURATION
  const titles = [
    { value: "bookName", keepOnMobile: true },
    { value: "quantity", keepOnMobile: true },
    { value: "bookType", keepOnMobile: true },
    { value: "academicLevel", keepOnMobile: true },
    { value: "singlePurchasePrice", keepOnMobile: true },
    { value: "singleSellPrice", keepOnMobile: true },
    { value: "info", keepOnMobile: true },
  ];

  const rows = filteredBooks?.map((book) => [
    {
      value: (
        <div className="o-row o-row--horizontal-align_center o-row--vertical-align_center">
          <Link to={`/product-page/${book.id}`} className={`link`}>
            {book?.name}
          </Link>
          {book?.amountInStock <= 5 && (
            <Tooltip content={t("productQuantityAboutToRunOut")}>
              <img src={infoBlue} className="tooltip-img" />
            </Tooltip>
          )}
        </div>
      ),
      type: TABLE_CELL_TYPES.ELEMENT,
      keepOnMobile: true,
    },
    {
      value: book?.amountInStock,
      type: TABLE_CELL_TYPES.TEXT,
      keepOnMobile: true,
    },
    {
      value: t(book?.productSubType),
      type: TABLE_CELL_TYPES.TEXT,
      keepOnMobile: true,
    },
    {
      value: book?.schoolClass?.name || book?.year,
      type: TABLE_CELL_TYPES.TEXT,
      keepOnMobile: true,
    },
    {
      value: `${book?.purchasePrice || 0} ${t("IQD")}`,
      type: TABLE_CELL_TYPES.TEXT,
      keepOnMobile: true,
    },
    {
      value: `${book?.price || 0} ${t("IQD")}`,
      type: TABLE_CELL_TYPES.TEXT,
      keepOnMobile: true,
    },
    {
      value: (
        <div className="o-row o-row--gap-size_200">
          <CustomButton
            variant="blank"
            icon={infoIcon}
            iconSize={19}
            title={t("info")}
            onClick={() => openEditBookModal(book)}
          />
        </div>
      ),
      type: TABLE_CELL_TYPES.ELEMENT,
      keepOnMobile: true,
    },
  ]);

  // USE EFFECTS
  useEffect(() => {
    getBooks();
  }, [pagination.currentPage, searchTerm, userData]);

  // RENDER
  return (
    <PageWrapper>
      <div
        className="view-books-page-container"
        style={{ direction: currentDirection }}
      >
        <div>
          <BackToDashboard
            title={t("back")}
            variant="blank-fit-content"
            icon={currentDirection === "ltr" ? previousIcon : nextIcon}
            iconSize={14}
            to={
              userData?.userType === "user" ? "/invoiceManagement" : `/school-dashboard`
            }
          />
        </div>

        {errorMessage && <ErrorBox>{t(errorMessage)}</ErrorBox>}

        <div className="o-row o-row--horizontal-align_space-between u-margin--top_700 u-margin--bottom_700">
          <PageHeadline title={t("theBooks")} />

          {userData?.userId && (
            <div className="o-row o-row--gap-size_300">
              <CustomButton
                title={t("addBook")}
                icon={addIcon}
                iconSize={20}
                onClick={openAddBookModal}
              />
            </div>
          )}
        </div>

        {/* SEARCH FUNCTIONALITY */}
        <section className="o-column-row-switcher o-row--gap-size_300 o-column--vertical-align_start">
          <div className="o-row o-row--vertical-align_center o-column--vertical-align_start o-row--gap-size_300 u-width_full-width">
            {selectedSearchType.value === "academicLevel" ? (
              <HotDropdownV2
                initOptions={[
                  ...educationLevels,
                  { title: t("selectAll"), value: "" },
                ]}
                selected={searchTerm?.title || ""}
                setSelected={(selected) => {
                  setSearchTerm(selected);
                }}
                page="books"
                getData={(page, limit, isDropDown, query) =>
                  fetchEducationLevels(page, limit, isDropDown, query)
                }
                optionsName="classes"
                thick
                key="academic-level-dropdown"
                flex
              />
            ) : selectedSearchType.value === "bookType" ? (
              <Dropdown
                options={[
                  { title: t("governmental"), value: "governmental" },
                  { title: t("enrichment"), value: "enrichment" },
                  { title: t("selectAll"), value: "" },
                ]}
                selected={searchTerm}
                setSelected={(selected) => setSearchTerm(selected)}
                thick
                flex
              />
            ) : (
              <CustomInput
                placeholder={`${t("searchTitle")} ${t(
                  selectedSearchType?.value
                )}`}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                resetSearch={() => {
                  setSearchQuery("");
                  handleSearch("");
                }}
                thick
                flex
              />
            )}
          </div>
          <div className="o-row o-row--horizontal-align_start o-row--gap-size_300 u-width_full-width">
            <div className="">
              <Dropdown
                options={[
                  { title: t("bookName"), value: "bookName" },
                  { title: t("bookType"), value: "bookType" },
                  { title: t("academicLevel"), value: "academicLevel" },
                ]}
                selected={selectedSearchType}
                setSelected={handleSearchType}
                thick
                flex
              />
            </div>
            <CustomButton
              title={t("search")}
              onClick={() => handleSearch()}
              icon={searchIcon}
              iconSize={15}
              classList="c-search-button"
            />
          </div>
        </section>

        {/* BOOKS TABLE */}
        {loading ? (
          <Loader />
        ) : filteredBooks.length === 0 ? (
          <InformationBox>
            <p>{t("noBooksFound")}</p>
          </InformationBox>
        ) : (
          <div className="u-margin--top_400">
            {errorMessage && <ErrorMessage>{errorMessage}</ErrorMessage>}
            <RoundedTable
              titles={titles}
              rows={rows}
              fullData={filteredBooks}
              pagination={pagination}
              setPagination={setPagination}
              selectedRows={selectedBooks}
              setSelectedRows={setSelectedBooks}
            />
          </div>
        )}

        <AddBookModal
          isOpen={showAddBookModal}
          onClose={() => setShowAddBookModal(false)}
          onBookCreated={getBooks}
          setPagination={setPagination}
        />

        <EditBookModal
          isOpen={showEditBookModal}
          onClose={() => setShowEditBookModal(false)}
          book={selectedBook}
          onBookUpdated={getBooks}
          setPagination={setPagination}
        />

        {showDeleteConfirmation && (
          <AreYouSureModal
            onClose={() => setShowDeleteConfirmation(false)}
            onConfirm={() => handleDeleteBook(selectedBook.id)}
            customMessage={t("deleteBook")}
            closeTitle="cancel"
            confirmTitle="confirm"
            loading={loadingModal}
            cancel
            confirmIconType="whiteCheck"

          />
        )}
      </div>
    </PageWrapper>
  );
}
